<script setup lang="ts">
/**
 * Generic table cell for plain text or a numeric value.
 *
 * Renders the raw `value` as-is. Shows `emptyPlaceholder` (in muted style)
 * when `value` is `null`, `undefined`, `NaN`, or a blank string. Supports
 * a default `<slot>` override for callers that need custom inner markup.
 */
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    /** Plain text to display. */
    value?: string | number | null
    /** Max width of the cell (CSS value). Set by DynamicTable from column config. */
    maxWidth?: string
    /** Placeholder shown when `value` is missing or blank; rendered in muted style. Leave empty to render nothing. */
    emptyPlaceholder?: string
  }>(),
  { emptyPlaceholder: '' },
)

const isEmpty = computed(() => {
  if (props.value == null) return true
  if (typeof props.value === 'number') {
    return Number.isNaN(props.value)
  }
  return props.value.trim() === ''
})
</script>

<template>
  <td
    class="font-tufte text-tufte-text"
    :style="props.maxWidth ? { maxWidth: props.maxWidth, overflowWrap: 'break-word' } : undefined"
  >
    <slot>
      <span v-if="!isEmpty">{{ value }}</span>
      <span v-else-if="emptyPlaceholder !== ''" class="text-tufte-muted">{{
        emptyPlaceholder
      }}</span>
    </slot>
  </td>
</template>
